// # Calendar Timeline View Component
// Horizontal timeline visualization of events

'use client';

import React, { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import type { CalendarEvent } from '../../types/calendar';
import { CalendarEventChip } from './CalendarEventChip';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';

interface CalendarTimelineViewProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  onCreateEvent?: (date: Date) => void;
  className?: string;
}

export function CalendarTimelineView({
  events,
  onEventClick,
  onDateClick,
  onCreateEvent,
  className = ''
}: CalendarTimelineViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { isMobile } = useDeviceInfo();

  // # Get days for timeline (2 weeks)
  const getDaysToDisplay = useCallback((): Date[] => {
    const days: Date[] = [];
    const startDate = new Date(currentDate);
    startDate.setDate(startDate.getDate() - 7); // Start 1 week before
    
    for (let i = 0; i < 14; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    
    return days;
  }, [currentDate]);

  // # Format date helper
  const formatDate = useCallback((date: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // # Get events for a specific date
  const getEventsForDate = useCallback((date: Date): CalendarEvent[] => {
    if (!events || events.length === 0) return [];
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return [];
    
    const dateStr = formatDate(date);
    
    return events.filter(event => {
      if (!event || !event.start_date || !event.end_date) return false;
      const normalizedStart = event.start_date.split('T')[0];
      const normalizedEnd = event.end_date.split('T')[0];
      return normalizedStart <= dateStr && normalizedEnd >= dateStr;
    });
  }, [events, formatDate]);

  // # Check if date is today
  const isToday = useCallback((date: Date): boolean => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  // # Navigation handlers
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 14);
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 14);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // # Get current period label
  const getCurrentPeriodLabel = (): string => {
    const days = getDaysToDisplay();
    const start = days[0];
    const end = days[days.length - 1];
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const days = getDaysToDisplay();

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Header */}
      <div className={`flex-shrink-0 border-b border-gray-100 ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
        <div className="flex items-center justify-between">
          <h2 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
            {getCurrentPeriodLabel()}
          </h2>
          <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-1.5'}`}>
            {!isMobile && (
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-all duration-200 flex items-center gap-1.5"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                <span>Today</span>
              </button>
            )}
            <div className={`flex items-center bg-gray-100 rounded-md ${isMobile ? 'gap-0 p-0.5' : 'gap-0.5 p-0.5'}`}>
              <button
                onClick={goToPrevious}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Previous"
              >
                <ChevronLeft className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
              <button
                onClick={goToNext}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Next"
              >
                <ChevronRight className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div 
        className="flex-1 overflow-x-auto overflow-y-hidden"
        style={{ 
          touchAction: 'pan-x pan-y',
          userSelect: 'none'
        }}
      >
        <div className={`flex h-full ${isMobile ? 'min-w-[1400px]' : 'min-w-[1600px]'}`}>
          {days.map((date, index) => {
            if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
              return null;
            }
            
            const dateEvents = getEventsForDate(date);
            const isTodayDate = isToday(date);
            const isSelected = selectedDate && formatDate(selectedDate) === formatDate(date);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            return (
              <div
                key={`${date.toISOString()}-${index}`}
                className={`flex-shrink-0 border-r border-gray-200 flex flex-col ${
                  isMobile ? 'w-[100px]' : 'w-[120px]'
                } ${
                  isTodayDate
                    ? 'bg-blue-50 border-blue-300'
                    : isWeekend
                    ? 'bg-gray-50'
                    : 'bg-white'
                }`}
              >
                {/* Date header */}
                <div
                  className={`flex-shrink-0 border-b border-gray-200 p-2 cursor-pointer transition-colors ${
                    isTodayDate
                      ? 'bg-blue-100'
                      : isSelected
                      ? 'bg-blue-50'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setSelectedDate(date);
                    onDateClick?.(date);
                  }}
                >
                  <div className="text-center">
                    <div className={`font-semibold ${
                      isMobile ? 'text-xs' : 'text-sm'
                    } ${
                      isTodayDate ? 'text-blue-700' : 'text-gray-700'
                    }`}>
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`font-bold ${
                      isMobile ? 'text-lg' : 'text-xl'
                    } ${
                      isTodayDate ? 'text-blue-700' : 'text-gray-900'
                    }`}>
                      {date.getDate()}
                    </div>
                    <div className={`text-xs ${
                      isTodayDate ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      {date.toLocaleDateString('en-US', { month: 'short' })}
                    </div>
                  </div>
                </div>

                {/* Timeline events */}
                <div className="flex-1 relative overflow-y-auto">
                  {/* Hour markers */}
                  <div className="absolute inset-0">
                    {Array.from({ length: 24 }, (_, hour) => (
                      <div
                        key={hour}
                        className={`border-t border-gray-100 ${
                          isMobile ? 'h-[40px]' : 'h-[50px]'
                        }`}
                      >
                        {hour % 4 === 0 && (
                          <div className={`text-[10px] text-gray-400 px-1 ${
                            isMobile ? 'pt-0.5' : 'pt-1'
                          }`}>
                            {hour.toString().padStart(2, '0')}:00
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Events positioned by time */}
                  <div className="relative z-10">
                    {dateEvents.map((event) => {
                      // Calculate position based on time
                      let top = 0;
                      let height = isMobile ? 40 : 50;
                      
                      if (event.start_time && !event.all_day) {
                        const [hours, minutes] = event.start_time.split(':').map(Number);
                        top = (hours * (isMobile ? 40 : 50)) + (minutes / 60) * (isMobile ? 40 : 50);
                      }
                      
                      if (event.end_time && !event.all_day) {
                        const [startHours, startMinutes] = event.start_time?.split(':').map(Number) || [0, 0];
                        const [endHours, endMinutes] = event.end_time.split(':').map(Number);
                        const startMinutesTotal = startHours * 60 + startMinutes;
                        const endMinutesTotal = endHours * 60 + endMinutes;
                        const duration = endMinutesTotal - startMinutesTotal;
                        height = (duration / 60) * (isMobile ? 40 : 50);
                      }

                      return (
                        <div
                          key={event.id}
                          style={{
                            top: `${top}px`,
                            height: `${Math.max(height, isMobile ? 30 : 40)}px`,
                          }}
                          className="absolute left-0 right-0 px-1"
                        >
                          <CalendarEventChip
                            event={event}
                            onClick={onEventClick}
                            compact={true}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Event count badge */}
                {dateEvents.length > 0 && (
                  <div className={`flex-shrink-0 border-t border-gray-200 p-1 text-center ${
                    isTodayDate ? 'bg-blue-100' : 'bg-gray-50'
                  }`}>
                    <span className={`text-xs font-semibold ${
                      isTodayDate ? 'text-blue-700' : 'text-gray-600'
                    }`}>
                      {dateEvents.length} event{dateEvents.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CalendarTimelineView;

